import { useT } from "../lib/i18n.jsx";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { IconCheck, IconX, IconArrow, IconDot } from "../components/icons.jsx";

/* ─────────────────────────────────────────────────────────────
   Setup wizard layout: aside with step indicator + main content
   ───────────────────────────────────────────────────────────── */

const STEPS = [
  { key: "db",         label: "Database connectivity" },
  { key: "migrations", label: "Schema migration" },
  { key: "admin",      label: "Administrator account" },
  { key: "plans",      label: "Plans & quotas" },
  { key: "finalize",   label: "Finalize installation" },
];

export default function SetupPage({ state, onComplete }) {
  const { t } = useT();
  const [currentStep, setCurrentStep] = useState(state.step);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCurrentStep(state.step);
  }, [state.step]);

  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className="auth">
      <aside className="auth-aside">
        <div className="auth-aside-top">
          <div className="auth-brand">
            vorks<span className="con">·connect</span>
          </div>
          <div className="eyebrow" style={{ marginTop: 18 }}>
            Installation
          </div>

          <h1 className="auth-headline">
            First-run <em>setup</em>.
          </h1>
          <p className="auth-sub">
            A few configuration steps before your control plane is ready.
            Everything you set here can be changed later from the admin
            settings.
          </p>

          <div className="wiz-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={
                  "wiz-step " +
                  (i < stepIndex ? "done" : i === stepIndex ? "current" : "")
                }
              >
                <span className="num">
                  {i < stepIndex ? <IconCheck size={11} /> : i + 1}
                </span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="auth-footer auth-aside-bottom">
          <span>{state.checks ? Object.values(state.checks).filter(Boolean).length : 0}/4 ready</span>
          <span>step {Math.min(stepIndex + 1, STEPS.length)}/{STEPS.length}</span>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {currentStep === "db" && <StepDb state={state} />}
              {currentStep === "migrations" && <StepMigrations state={state} />}
              {currentStep === "admin" && (
                <StepAdmin
                  busy={busy}
                  setBusy={setBusy}
                  onNext={() => setCurrentStep("plans")}
                />
              )}
              {currentStep === "plans" && (
                <StepPlans
                  busy={busy}
                  setBusy={setBusy}
                  onNext={() => setCurrentStep("finalize")}
                />
              )}
              {currentStep === "finalize" && (
                <StepFinalize
                  busy={busy}
                  setBusy={setBusy}
                  onDone={onComplete}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

/* ─────────── individual steps ─────────── */

function StepDb({ state }) {
  return (
    <>
      <div className="eyebrow">Step 01</div>
      <h1>Database connectivity</h1>
      <p className="sub">
        The backend cannot reach your MariaDB/MySQL server. Check the
        connection settings in your environment variables and restart the
        application.
      </p>

      <div className="check-grid">
        <Check ok={state.checks?.db} label="Connection to DB host" />
      </div>

      <div className="error-line">
        Set <span className="mono">DB_HOST, DB_USER, DB_PASSWORD, DB_NAME</span>{" "}
        in the Plesk Node.js extension's "Custom environment variables", then
        restart the app. This page will advance automatically once the database
        is reachable.
      </div>
    </>
  );
}

function StepMigrations({ state }) {
  return (
    <>
      <div className="eyebrow">Step 02</div>
      <h1>Schema migration</h1>
      <p className="sub">
        Apply database migrations to create the required tables. Run this
        command once from SSH or via Plesk's "Run script" button:
      </p>

      <pre
        style={{
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r)",
          padding: 14,
          fontFamily: "var(--mono)",
          fontSize: 12,
          color: "var(--acc)",
          overflow: "auto",
        }}
      >
        npm run migrate
      </pre>

      <div className="check-grid">
        <Check ok={state.checks?.db} label="DB reachable" />
        <Check ok={state.checks?.migrations} label="Migrations applied" />
      </div>

      <p style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-faint)", marginTop: 18 }}>
        This page polls every 3 seconds. Once migrations are applied, you'll
        automatically move to the next step.
      </p>
    </>
  );
}

function StepAdmin({ busy, setBusy, onNext }) {
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/setup/admin", { method: "POST", body: form, auth: false });
      onNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="eyebrow">Step 03</div>
      <h1>Administrator</h1>
      <p className="sub">
        Create the first administrator account. This user will have full
        access to settings, users, plans and payment configuration.
      </p>

      <form onSubmit={submit}>
        <div className="field">
          <label>Display name</label>
          <input
            type="text"
            required
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>Password</label>
          <input
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            disabled={busy}
          />
          <span className="hint">Minimum 8 characters.</span>
        </div>

        <button className="btn primary full lg" type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create administrator"}
          {!busy && <IconArrow size={14} />}
        </button>
        {error && <div className="error-line">{error}</div>}
      </form>
    </>
  );
}

function StepPlans({ busy, setBusy, onNext }) {
  const [error, setError] = useState(null);

  async function useDefaults() {
    setBusy(true);
    setError(null);
    try {
      await api("/setup/plans", {
        method: "POST",
        body: { use_defaults: true },
        auth: false,
      });
      onNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="eyebrow">Step 04</div>
      <h1>Plans &amp; quotas</h1>
      <p className="sub">
        We'll pre-create three plans you can edit later: Free, Pro, and Team.
        Quotas, prices, and Stripe/PayPal IDs are all configurable in the
        admin settings.
      </p>

      <div style={{ display: "grid", gap: 8, margin: "20px 0 24px" }}>
        <PlanPreview name="Free" devices={1} viewers={1} price="—" badge="default" />
        <PlanPreview name="Pro" devices={5} viewers={3} price="9,99 €/mo" />
        <PlanPreview name="Team" devices={25} viewers={10} price="29,99 €/mo" />
      </div>

      <button className="btn primary full lg" onClick={useDefaults} disabled={busy}>
        {busy ? "Creating plans…" : "Create default plans"}
        {!busy && <IconArrow size={14} />}
      </button>
      {error && <div className="error-line">{error}</div>}
    </>
  );
}

function PlanPreview({ name, devices, viewers, price, badge }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: 16,
        alignItems: "center",
        padding: "14px 16px",
        background: "var(--bg)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r)",
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 10 }}>
          {name}
          {badge && <span className="chip acc" style={{ fontSize: 9.5 }}>{badge}</span>}
        </div>
        <div className="mono" style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 3 }}>
          {devices} devices · {viewers} parallel viewers
        </div>
      </div>
      <div className="mono" style={{ fontSize: 12, color: "var(--text-mute)" }}>{price}</div>
    </div>
  );
}

function StepFinalize({ busy, setBusy, onDone }) {
  const [error, setError] = useState(null);

  async function finalize() {
    setBusy(true);
    setError(null);
    try {
      await api("/setup/finalize", { method: "POST", auth: false });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="eyebrow">Step 05</div>
      <h1>Ready to go</h1>
      <p className="sub">
        Everything is in place. After finalizing, the setup endpoints will be
        locked. You can sign in with the administrator credentials you just
        created.
      </p>

      <div className="check-grid">
        <Check ok label="Database" />
        <Check ok label="Schema migrated" />
        <Check ok label="Administrator created" />
        <Check ok label="Plans available" />
      </div>

      <button
        className="btn primary full lg"
        onClick={finalize}
        disabled={busy}
        style={{ marginTop: 12 }}
      >
        {busy ? "Locking setup…" : "Finalize & continue"}
        {!busy && <IconArrow size={14} />}
      </button>
      {error && <div className="error-line">{error}</div>}
    </>
  );
}

function Check({ ok, label }) {
  return (
    <div className={"check " + (ok === true ? "pass" : ok === false ? "fail" : "pending")}>
      {ok === true ? <IconCheck className="icon" /> : ok === false ? <IconX className="icon" /> : <IconDot className="icon" size={12} />}
      <span>{label}</span>
    </div>
  );
}
